export const emptyDay = () => ({ sessions: [] });

export const emptyPlan = () => ({
  planId: null,
  name: "",
  weekStart: "",
  unitSystem: "metric",
  goal: "performance",
  // Master toggle set on Step 2. `null` = undecided (default for new plans —
  // dietary sections stay hidden until the athlete explicitly picks). `true` =
  // full meal planning with pathways + shopping. `false` = targets only, skip
  // Pathways + Shopping steps.
  mealPlanning: null,
  profile: {
    name: "",
    sex: "male",
    age: "",
    dob: "",
    weight: "", // metric kg
    height: "", // metric cm
    weightUnit: "kg",
    heightUnit: "cm",
    activity: "active",
    trainingSupport: "balanced",
    cookingStyle: "flexible",
    householdSize: 1,
    hydrationBaseline: 1.5, // litres — drives in-session fluid targets only
    shoppingRegion: "UK", // pack-size table for shopping list rounding
    // TrainingPeaks opt-in — null = ask once, true = show connect card, false = minimise everywhere.
    useTP: null,
    // How the athlete plans to use SPC. null = ask once. 'full' = whole wizard
    // (plan generation). 'guidelines' = macro targets only (no meal plans).
    // 'tracker_only' = skip Training / Plan / Pathways / Shopping entirely.
    usage: null,
  },
  dietary: {
    pattern: "omnivore",
    mealStyle: "meals",
    exclusions: [],
    customExclusions: [],
    approvedProducts: [],
  },
  macros: { mode: "auto", protein: 1.8, carb: 6.0, fat: 0.8 },
  prevWeek: { days: Array.from({ length: 7 }, () => ({ sessions: [] })) },
  trainingWeek: { days: Array.from({ length: 7 }, () => ({ sessions: [] })) },
  nextWeek: { days: Array.from({ length: 7 }, () => ({ sessions: [] })) },
  raceWeek: { enabled: false, dayIndex: 6, event: "", strategy: "", carbsPerHour: 60 },
  selections: {},
  completions: {},
  generated: { targets: null, dayPathways: null, shopping: null },
  currentStep: 1,
});

// Step schema v2 (June 2026): "Setup" was split out of Profile/Dietary so no
// single screen asks for everything at once. Plans saved under v1 are migrated
// on load (see OLD_TO_NEW_STEP in Wizard.jsx).
export const STEP_SCHEMA = 2;

export const STEPS = [
  { n: 1, key: "profile", label: "Profile" },
  { n: 2, key: "setup", label: "Setup" },
  { n: 3, key: "dietary", label: "Dietary" },
  { n: 4, key: "macros", label: "Targets" },
  { n: 5, key: "training", label: "Training" },
  { n: 6, key: "plan", label: "Plan" },
  { n: 7, key: "pathways", label: "Pathways" },
  { n: 8, key: "shopping", label: "Shopping" },
  { n: 9, key: "summary", label: "Summary" },
];

export const GOALS = [
  { value: "performance", label: "Performance" },
  { value: "maintenance", label: "Maintenance" },
  { value: "fat-loss", label: "Controlled Fat Loss" },
  { value: "recovery", label: "Recovery" },
];

export const ACTIVITY = [
  { value: "desk", label: "Low / desk-based" },
  { value: "active", label: "Generally active" },
  { value: "active_job", label: "Active occupation" },
  { value: "very_active", label: "Very active occupation" },
];

export const COOKING_STYLES = [
  { value: "flexible", label: "Flexible", hint: "A varied menu across the week" },
  { value: "batch", label: "Batch Cooker", hint: "Same lunch & dinner Mon–Fri, varied at the weekend" },
  { value: "family", label: "Family Meals", hint: "Simple, shareable meals" },
  { value: "solo", label: "Solo", hint: "Single-portion, low-faff meals" },
];


export const TRAINING_SUPPORT = [
  { value: "conservative", label: "Conservative" },
  { value: "balanced", label: "Balanced" },
  { value: "full", label: "Full Fuelling" },
];

export const SPORTS = [
  { value: "run", label: "Running" },
  { value: "cycle", label: "Cycling" },
  { value: "swim", label: "Swimming" },
  { value: "walk", label: "Walking / Hiking" },
  { value: "triathlon", label: "Triathlon" },
  { value: "team", label: "Team Sport" },
  { value: "strength", label: "Strength" },
  { value: "other", label: "Other" },
];

export const INTENSITY = [
  { value: "recovery", label: "Recovery" },
  { value: "easy", label: "Easy" },
  { value: "endurance", label: "Endurance" },
  { value: "tempo", label: "Tempo" },
  { value: "threshold", label: "Threshold" },
  { value: "vo2max", label: "VO2 Max" },
  { value: "intervals", label: "Intervals" },
  { value: "long", label: "Long" },
  { value: "race", label: "Race" },
];

export const DIET_PATTERNS = [
  { value: "omnivore", label: "Omnivore" },
  { value: "pescatarian", label: "Pescatarian" },
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
];

// Bold sport palette — SPC yellow is hero, cyan / coral / lime carry the
// data. Reads like Strava/WHOOP: punchy, high-contrast, unapologetic.
export const DEMAND_COLOR = {
  rest: "#71717A",         // neutral graphite
  low: "#22D3EE",          // electric cyan
  moderate: "#E2E30A",     // SPC yellow
  high: "#FB7185",         // hot coral
  very_high: "#EF4444",    // deep crimson
};
